import type { CostFileTemplate } from '../../../../../../shared/types/roi-hub/fileIngestion.types';
import type { AdPartner } from '../../../../main/types/adPartnerTypes';
import {
  OMIT_FIELD,
  OPTIONAL_BRANCH_FIELD_OPTIONS,
  REQUIRED_BRANCH_FIELD_OPTIONS,
} from '../constants/costFileTemplateBranchFieldOptions';
import type { CostFileTemplateCreatorState, GroupOption } from '../types';

export type EditCostFileTemplatePayload = Pick<CostFileTemplateCreatorState, 'id' | 'title' | 'adPartner' | 'mapping'>;

export type CostFileSamplePayload = Pick<CostFileTemplateCreatorState, 'sampleFile' | 'mapping'>;

export type CostFileTemplateDto = Pick<CostFileTemplate, 'name' | 'ad_network' | 'mapping'> & {
  id?: string;
};

const OPTION_LOOKUP: Record<string, GroupOption> = {};

export class CostFileTemplateAdapterService {
  static get BRANCH_OPTION_LOOKUP() {
    if (Object.keys(OPTION_LOOKUP).length === 0) {
      REQUIRED_BRANCH_FIELD_OPTIONS.forEach((option) => {
        OPTION_LOOKUP[option.value] = option;
      });

      OPTIONAL_BRANCH_FIELD_OPTIONS.forEach((option) => {
        OPTION_LOOKUP[option.value] = option;
      });
    }

    return OPTION_LOOKUP;
  }

  apiToEditPayload(apiCostFileTemplate: CostFileTemplate, adPartners: AdPartner[]): EditCostFileTemplatePayload {
    const mapping = apiCostFileTemplate.mapping.fields?.map(({ source, target }) => {
      const option = CostFileTemplateAdapterService.BRANCH_OPTION_LOOKUP[target] || OMIT_FIELD;
      return {
        sourceKey: source,
        branchKeyOption: { ...option },
      };
    });

    const adPartner = adPartners.find((partner) => partner.machine_name === apiCostFileTemplate.ad_network);

    return {
      id: apiCostFileTemplate.id,
      title: apiCostFileTemplate.name,
      adPartner: {
        label: adPartner?.company_name || apiCostFileTemplate.ad_network,
        value: adPartner?.machine_name || apiCostFileTemplate.ad_network,
      } as any,
      mapping: mapping || [],
    };
  }

  fileToSample(csvFile: File | undefined, csvJson: Record<string, string>[]): CostFileSamplePayload {
    return {
      sampleFile: {
        uploadedFile: csvFile,
        parsedCSV: csvJson,
      },
      mapping: Object.keys(csvJson[0] || {}).map((sourceKey) => ({
        sourceKey,
        branchKeyOption: undefined,
      })),
    };
  }

  stateMappingToApiPayload(mapping: CostFileTemplateCreatorState['mapping']): CostFileTemplateDto['mapping'] {
    return {
      fields: mapping.map(({ sourceKey, branchKeyOption }) => ({
        source: sourceKey,
        target: branchKeyOption?.value || OMIT_FIELD.value,
      })),
    };
  }

  stateToApiPayload(state: CostFileTemplateCreatorState): CostFileTemplateDto {
    return {
      id: state.id,
      name: state.title,
      ad_network: state.adPartner?.value || '',
      mapping: this.stateMappingToApiPayload(state.mapping),
    };
  }
}
